import { useMutation } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'

import { Button } from '@ui/components/Button'
import { ContentBlock } from '@ui/components/ContentBlock/ContentBlock'
import { FeatureIcon } from '@ui/components/FeatureIcon/FeatureIcon'
import { Input } from '@ui/components/Input'
import { Modal } from '@ui/components/Modal'
import { WarningIcon } from '@ui/icons/Warning.icon'

import { getClientPrimaryEmail } from '@utils/AccountAccess/accountEmailStatuses'
import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { getTickmillPublicDomain } from '@utils/companyName.utils'

import { useScrollToTop } from '@hooks/useScrollToTop'

import { Box } from '@shared/components/Box'
import { InformationModal } from '@shared/modals/Information.modal'
import { FullScreenFooter } from '@shared/templates/FullScreenLayout'

const useResetCompromisedPassword = (email: string | undefined) => {
  const { t } = useTranslation()
  const apiClient = useApiClient(ClientApiClient)
  const navigate = useNavigate()

  const passwordSentModal = useModal(InformationModal, {
    title: t('Password has been reset'),
    onClose: () => navigate('/logout'),
  })

  const { mutate: handleSubmit, isPending } = useMutation({
    mutationFn: async () => {
      if (!email) throw new Error('Email is not defined')

      await apiClient.resetPassword({
        email,
        communicationLanguageId: 'en',
        tickmillPublicDomain: getTickmillPublicDomain(),
      })

      const params = new URLSearchParams({ email })
      await apiClient.getResetPasswordAttempts(params)
    },
    onSuccess: () => {
      passwordSentModal.open({
        description: () => (
          <Modal.Body.Text>
            {t('We sent out the password reset e-mail')} (<b>{email}</b>){' '}
            {t('to your address. Please follow the instructions on e-mail.')}
          </Modal.Body.Text>
        ),
      })
    },
  })

  return {
    handleSubmit,
    isPending,
  }
}

export const CompromisedPassword = () => {
  useScrollToTop()

  const { t } = useTranslation()
  const { account } = useAccountReadContext()
  const email = account && getClientPrimaryEmail(account)

  const { handleSubmit, isPending } = useResetCompromisedPassword(email)

  return (
    <Box flex direction='column' gap='x32' align='center'>
      <ContentBlock
        variant='voucher'
        icon={
          <FeatureIcon
            size='lg'
            icon={WarningIcon}
            bg='surface-primary-med-em'
            color='text-white'
          />
        }
        title={t('Action Required')}
        description={[
          t('Password compromised.body'),
          t('Password compromised.body_secondary'),
          <Input label={t('Email Address')} value={email} disabled fullWidth />,
        ]}
      />
      <FullScreenFooter>
        <Button.Primary fullWidth size='lg' disabled={isPending} onClick={() => handleSubmit()}>
          {t('Reset Password')}
        </Button.Primary>
      </FullScreenFooter>
    </Box>
  )
}
